import {
  differenceInMinutes,
  differenceInSeconds,
  differenceInDays,
  differenceInCalendarDays,
  differenceInWeeks,
  differenceInMonths,
  differenceInYears,
  differenceInHours,
  startOfSecond,
  startOfMinute,
  startOfHour,
  startOfDay,
  startOfWeek,
  startOfMonth,
  startOfYear,
  endOfSecond,
  endOfMinute,
  endOfHour,
  endOfDay,
  endOfWeek,
  endOfMonth,
  endOfYear,
  getSeconds,
  getMinutes,
  getHours,
  getDay,
  getWeek,
  getMonth,
  getYear,
  differenceInCalendarWeeks,
  differenceInCalendarMonths,
  differenceInCalendarYears,
  setSeconds,
  setMinutes,
  setHours,
  setDay,
  setYear,
  setMonth,
  setWeek,
} from 'date-fns';

import { CalendarUnit, TimeUnit } from './types';

export const DIFFERENCE_IN_MAP: Record<
  TimeUnit,
  (dateLeft: Date, dateRight: Date) => number
> = {
  seconds: differenceInSeconds,
  minutes: differenceInMinutes,
  hours: differenceInHours,
  days: differenceInDays,
  weeks: differenceInWeeks,
  months: differenceInMonths,
  years: differenceInYears,
};

export const DIFFERENCE_IN_CALENDAR_TIME_MAP: Record<
  CalendarUnit,
  (dateLeft: Date, dateRight: Date) => number
> = {
  days: differenceInCalendarDays,
  weeks: differenceInCalendarWeeks,
  months: differenceInCalendarMonths,
  years: differenceInCalendarYears,
};

export const START_OF_MAP: Record<TimeUnit, (date: Date) => Date> = {
  seconds: startOfSecond,
  minutes: startOfMinute,
  hours: startOfHour,
  days: startOfDay,
  weeks: startOfWeek,
  months: startOfMonth,
  years: startOfYear,
};

export const END_OF_MAP: Record<TimeUnit, (date: Date) => Date> = {
  seconds: endOfSecond,
  minutes: endOfMinute,
  hours: endOfHour,
  days: endOfDay,
  weeks: endOfWeek,
  months: endOfMonth,
  years: endOfYear,
};

export const GET_TIME_MAP: Record<TimeUnit, (date: Date) => number> = {
  seconds: getSeconds,
  minutes: getMinutes,
  hours: getHours,
  days: getDay,
  weeks: getWeek,
  months: getMonth,
  years: getYear,
};

export const SET_TIME_MAP: Record<
  TimeUnit,
  (date: Date, value: number) => Date
> = {
  seconds: setSeconds,
  minutes: setMinutes,
  hours: setHours,
  days: setDay,
  weeks: setWeek,
  months: setMonth,
  years: setYear,
};

const Era: Record<string, string> = {
  G: 'G',
};

const CalendarYear: Record<string, string> = {
  y: 'y',
  yo: 'yo',
};

const LocalWeekNumberingYear: Record<string, string> = {
  Y: 'Y',
  Yo: 'Yo',
};

const ISOWeekNumberingYear: Record<string, string> = {
  R: 'R',
};

const ExtendedYear: Record<string, string> = {
  u: 'u',
};

const Quarter: Record<string, string> = {
  Q: 'Q',
  Qo: 'Qo',
};

const QuarterStandAlone: Record<string, string> = {
  q: 'q',
  qo: 'qo',
};

const Month: Record<string, string> = {
  M: 'M',
  Mo: 'Mo',
};

const MonthStandAlone: Record<string, string> = {
  L: 'L',
  Lo: 'Lo',
};

const LocalWeekOfYear: Record<string, string> = {
  w: 'w',
  wo: 'wo',
};

const ISOWeekOfYear: Record<string, string> = {
  I: 'I',
  Io: 'Io',
};

const DayOfMonth: Record<string, string> = {
  d: 'd',
  do: 'do',
};

const DayOfYear: Record<string, string> = {
  D: 'D',
  Do: 'Do',
};

const DayOfWeek: Record<string, string> = {
  E: 'E',
};

const ISODayOfWeek: Record<string, string> = {
  i: 'i',
  io: 'io',
};

const LocalDayOfWeek: Record<string, string> = {
  e: 'e',
  eo: 'eo',
};

const LocalDayOfWeekStandAlone: Record<string, string> = {
  c: 'c',
  co: 'co',
};

const AMPM: Record<string, string> = {
  a: 'a',
  b: 'b',
};

const FlexibleDayPeriod: Record<string, string> = {
  B: 'B',
};

const Hour: Record<string, string> = {
  h: 'h',
  ho: 'ho',
  H: 'H',
  Ho: 'Ho',
  K: 'K',
  Ko: 'Ko',
  k: 'k',
  ko: 'ko',
};

const Minute: Record<string, string> = {
  m: 'm',
  mo: 'mo',
};

const Second: Record<string, string> = {
  s: 's',
  so: 'so',
};

const FractionofSecond: Record<string, string> = {
  S: 'S',
};

const Timezone: Record<string, string> = {
  X: 'X',
  x: 'x',
  O: 'O',
  z: 'z',
};

const SecondsTimestamp: Record<string, string> = {
  t: 't',
};

const MillisecondsTimestamp: Record<string, string> = {
  T: 'T',
};

const LongLocalizedDate: Record<string, string> = {
  P: 'P',
};

const LongLocalizedTime: Record<string, string> = {
  p: 'p',
};

const CombinedDateandTime: Record<string, string> = {
  Pp: 'Pp',
};

export const DATE_FORMATS_MAP: Record<string, string> = {
  ...Era,
  ...CalendarYear,
  ...LocalWeekNumberingYear,
  ...ISOWeekNumberingYear,
  ...ExtendedYear,
  ...Quarter,
  ...QuarterStandAlone,
  ...Month,
  ...MonthStandAlone,
  ...LocalWeekOfYear,
  ...ISOWeekOfYear,
  ...DayOfMonth,
  ...DayOfYear,
  ...DayOfWeek,
  ...ISODayOfWeek,
  ...LocalDayOfWeek,
  ...LocalDayOfWeekStandAlone,
  ...AMPM,
  ...FlexibleDayPeriod,
  ...Hour,
  ...Minute,
  ...Second,
  ...FractionofSecond,
  ...Timezone,
  ...SecondsTimestamp,
  ...MillisecondsTimestamp,
  ...LongLocalizedDate,
  ...LongLocalizedTime,
  ...CombinedDateandTime,
};
